<template>
  <div class="asc-open-maintenance-bar-chart">
    <div class="asc-open-maintenance-bar-chart--header">
      <asc-header
        class="asc-open-maintenance-bar-chart--header-title"
        uppercase
        size="h4"
        value="Open Maintenance Recommendation Count by Tail"
      />
      <asc-tooltip
        :tooltip="getTooltip"
        bottom
        is-icon-tooltip
        :max-width="'300'"
        class="asc-open-maintenance-bar-chart--header-tooltip"
      >
        <span class="asc-icon">
          <v-icon class="icon-info" :size="20">mdi-information</v-icon>
        </span>
      </asc-tooltip>
    </div>
    <asc-fleet-bar-chart
      :tails="tails"
      :loading="loading"
      :is-issue-investigator="isIssueInvestigator"
      :is-issue-reader="isIssueReader"
      :is-issue-updater="isIssueUpdater"
      :series-data="getSeries"
      :selected-item="selectedItem"
      :max-length="getMaxLength"
      type="openMaintenance"
      @onSeriesClick="handleRowClicked"
    ></asc-fleet-bar-chart>

    <template v-if="!showAllTails && !loading">
      <div class="asc-open-maintenance-bar-chart--all-tails-button">
        <asc-accent-button
          button-text="See All Tails"
          secondary
          :on-click="handleSeeAllTailsClicked"
          icon="mdi-chevron-down"
          icon-position="left"
        ></asc-accent-button>
      </div>
    </template>
    <template v-if="showAllTails && !loading">
      <div class="asc-open-maintenance-bar-chart--all-tails-button">
        <asc-accent-button
          button-text="Collapse All Tails"
          secondary
          :on-click="handleCollapseAllTailsClicked"
          icon="mdi-chevron-up"
          icon-position="left"
        ></asc-accent-button>
      </div>
    </template>
  </div>
</template>

<script>
import AscHeader from '../atoms/AscHeader.vue'
import AscTooltip from '../atoms/AscTooltip.vue'
import AscAccentButton from '../atoms/AscAccentButton.vue'

import AscFleetBarChart from '../molecules/AscFleetBarChart.vue'
import _sumBy from 'lodash/sumBy'

export default {
  name: 'AscOpenMaintenanceBarChart',

  components: {
    AscAccentButton,
    AscTooltip,
    AscHeader,
    AscFleetBarChart,
  },

  props: {
    values: {
      type: Array,
      default: () => [],
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedItem: {
      type: String,
      default: '',
    },
    maintenanceStatuses: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      showAllTails: false,
      tails: [],
    }
  },

  computed: {
    getTooltip() {
      return !this.isIssueUpdater
        ? 'Visualization defaults to show the top 15 tails ranked by issue count.'
        : 'Visualization defaults to show the top 15 tails ranked by maintenance recommendation count.'
    },

    getSeries() {
      return this.maintenanceStatuses.map(status => {
        const data = this.tails.length
          ? this.tails.map(tail => {
              const foundStatusWithCount = tail.statusByIssueCount.find(
                st => st.statusName === status.name,
              )

              if (foundStatusWithCount) {
                return foundStatusWithCount.issueCount
              }
              return 0
            })
          : []

        return {
          name: status.name,
          data: data,
          color: status.styleColor,
        }
      })
    },

    getMaxLength() {
      if (this.tails.length) {
        const statusNames = this.maintenanceStatuses.map(status => status.name)
        const max = _sumBy(
          this.tails[0].statusByIssueCount.filter(status =>
            statusNames.includes(status.statusName),
          ),
          'issueCount',
        )

        if (this.isIssueUpdater) {
          return max * 0.1 + max
        } else {
          return max + max * 0.1
        }
      }

      return null
    },
  },

  watch: {
    values: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.tails = this.showAllTails ? newVal : newVal.slice(0, 15)
      },
    },
  },

  methods: {
    handleSeeAllTailsClicked() {
      this.showAllTails = true
      this.tails = this.values
    },

    handleCollapseAllTailsClicked() {
      this.showAllTails = false
      this.tails = this.values.slice(0, 15)
    },

    handleRowClicked(data) {
      this.$emit('onSeriesClick', data)
    },
  },
}
</script>

<style lang="scss">
.asc-open-maintenance-bar-chart {
  .asc-open-maintenance-bar-chart--all-tails-button {
    padding: 0 12px;
  }

  .asc-accent-button-container .asc-accent-button--accent.theme--light.v-btn {
    width: 100%;
  }

  .asc-icon {
    display: inline-block;
    top: -2px;
    padding-left: $spacing-sm;
    position: relative;
  }

  .asc-icon .icon-info {
    color: $accent-500;
    cursor: pointer;
    display: inline-block;
    font-size: $font-size-lrg;
  }

  .asc-open-maintenance-bar-chart {
    &--container {
      padding: 0 10px;
      height: 350px;
      overflow-y: auto;
    }

    &--header {
      display: flex;
      padding-top: 10px;
      padding-left: 10px;

      &-title {
        & .asc-header--h4 {
          font-size: 14px !important;
          font-weight: 500 !important;
          line-height: 20px !important;
          letter-spacing: 1px !important;
        }
      }
    }
  }
}
</style>
